import type { HTMLComponent } from "@commercekit/react";
import { useEffect, useMemo, useState, useCallback } from "react";
import WvCloseIcon from "../atoms/WvCloseIcon";
import type { ImageField } from "@prismicio/types";

const WvPhotoCarouselPreview: HTMLComponent<{
  images: ImageField[];
  activeIndex: number;
  onClose(): void;
}> = ({ images, activeIndex, onClose }) => {
  const imageCount = useMemo(() => images?.length || 0, [images?.length]);

  const [currentIndex, setCurrentIndex] = useState(activeIndex);

  const [activeSlides, setActiveSlides] = useState<number[]>([]);

  const [zoomClass, setZoomClass] = useState("");

  const [hideControls, setHideControls] = useState(false);

  const prevIndex = useMemo(() => {
    // estimated to allow loop effect
    let prev_index = currentIndex - 1;

    if (prev_index > -1) return prev_index;
    return imageCount + prev_index;
  }, [currentIndex, imageCount]);

  const nextIndex = useMemo(() => {
    // estimated to allow loop effect
    let next_index = currentIndex + 1;

    if (next_index < imageCount) return next_index;
    return next_index - imageCount;
  }, [currentIndex, imageCount]);

  const updateCurrentIndex = useCallback(
    (count: number) => {
      setHideControls(false);
      // estimated to be between 0 and image counts
      setCurrentIndex((index: number) => {
        let new_index = index + count;
        if (new_index < 0) return imageCount - 1;
        if (new_index >= imageCount) return 0;
        return new_index;
      });
    },
    [imageCount]
  );

  useEffect(() => {
    setActiveSlides((activeStates: number[]) => {
      //  updates the active index of renderred images (dom only renders image included in these list)
      if (activeStates.length === imageCount) return [...activeStates];

      let active_slides = [...activeStates];

      if (!active_slides.includes(currentIndex))
        active_slides.push(currentIndex);

      if (!active_slides.includes(prevIndex)) active_slides.push(prevIndex);

      if (!active_slides.includes(nextIndex)) active_slides.push(nextIndex);

      return active_slides;
    });
  }, [currentIndex, prevIndex, nextIndex, imageCount]);

  useEffect(() => {
    //Applied 10ms after the component mounts, to trigger a ZOOM EFFECT
    const triggerZoom = setTimeout(() => {
      setZoomClass("lg-complete");
    }, 10);

    const handleKeypress = (key: string) => {
      switch (key) {
        case "Escape":
          onClose();
          break;
        case "ArrowRight":
          updateCurrentIndex(1);
          break;
        case "ArrowLeft":
          updateCurrentIndex(-1);
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", ({ key }) => handleKeypress(key));

    return () => {
      clearTimeout(triggerZoom);
      window.removeEventListener("keydown", ({ key }) => handleKeypress(key));
    };
  }, [onClose, updateCurrentIndex]);

  const imageItem = (index: number, url: string, alt: string) => {
    // returns the image with the right classess
    if (!activeSlides.includes(index))
      return <div className="lg-item " key={index}></div>;
    let extra_classes = `lg-item lg-loaded ${zoomClass}
        `;
    if (index === currentIndex)
      extra_classes = extra_classes.concat("lg-current ");
    if (index === prevIndex)
      extra_classes = extra_classes.concat("lg-prev-slide ");
    if (index === nextIndex)
      extra_classes = extra_classes.concat("lg-next-slide ");

    return (
      <div
        onMouseMove={() => {
          setHideControls(false);
        }}
        className={extra_classes}
        key={index}
      >
        <div className="lg-img-wrap">
          <img className="lg-object lg-image" alt={alt} src={url} />
        </div>
      </div>
    );
  };

  useEffect(() => {
    const show_controls_trigger = setTimeout(() => {
      setHideControls(true);
    }, 5000);
    return () => clearTimeout(show_controls_trigger);
  }, [hideControls]);

  return (
    <>
      <div
        className="lg-backdrop in"
        style={{
          transitionDuration: "150ms",
        }}
      ></div>

      <div
        tabIndex={-1}
        aria-modal="true"
        role="dialog"
        className={"lg-outer lg-start-zoom lg-use-css3 lg-css3 lg-slide lg-grab lg-show-after-load lg-visible".concat(
          hideControls ? " lg-hide-items" : ""
        )}
      >
        <div
          className="lg"
          style={{
            width: "100%",
            height: "100%",
          }}
        >
          <div
            className="lg-inner"
            style={{
              transitionTimingFunction: "ease",
              transitionDuration: "600ms",
            }}
          >
            {images?.map((item, index) =>
              imageItem(index, item.url || "", item.alt || "")
            )}
          </div>
          <div className="lg-toolbar lg-group">
            <button
              type="button"
              aria-label="Close gallery"
              className="close-icon lg-icon"
              onClick={onClose}
            >
              <WvCloseIcon size={15} />
            </button>

            <div id="lg-counter" role="status" aria-live="polite">
              <span id="lg-counter-current"> {currentIndex + 1}</span> /
              <span id="lg-counter-all"> {imageCount}</span>
            </div>
          </div>

          {imageCount > 1 ? (
            <div className="lg-actions">
              <button
                type="button"
                aria-label="Previous slide"
                data-icon-after=""
                className="lg-prev lg-icon"
                onClick={() => {
                  updateCurrentIndex(-1);
                }}
              >
                {" "}
              </button>

              <button
                type="button"
                aria-label="Next slide"
                className="lg-next lg-icon"
                data-icon-before=""
                onClick={() => {
                  updateCurrentIndex(1);
                }}
              ></button>
            </div>
          ) : null}

          <div role="status" aria-live="polite" className="lg-sub-html">
          </div>
        </div>
      </div>
    </>
  );
};

export default WvPhotoCarouselPreview;
